import { useEffect, useMemo, useRef, useState } from "react";

export default function CustomDropdown({
  id,
  options,
  value,
  onChange,
  placeholder = "Select option",
  className = "",
  searchable = false,
  disabled = false,
  menuPlacement = "bottom"
}) {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedOption = useMemo(
    () => options.find((option) => String(option.value) === String(value)),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    if (!searchable) {
      return options;
    }
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return options;
    }
    return options.filter((option) =>
      String(option.label).toLowerCase().includes(normalized)
    );
  }, [options, query, searchable]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  useEffect(() => {
    if (open && searchable && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open, searchable]);

  const handleToggle = () => {
    if (disabled) {
      return;
    }
    setOpen((previous) => !previous);
  };

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
    setQuery("");
  };

  return (
    <div
      ref={rootRef}
      className={`custom-dropdown ${className} ${open ? "open" : ""} ${
        menuPlacement === "top" ? "menu-top" : "menu-bottom"
      }`.trim()}
    >
      <button
        id={id}
        type="button"
        className="custom-dropdown-trigger"
        onClick={handleToggle}
        disabled={disabled}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setOpen(false);
          }
        }}
      >
        <span className="custom-dropdown-value">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="custom-dropdown-arrow" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path
              d="M6 9l6 6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open && (
        <div className="custom-dropdown-menu">
          {searchable && (
            <input
              ref={searchRef}
              className="custom-dropdown-search"
              placeholder="Search..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          )}

          <div className="custom-dropdown-options" role="listbox">
            {filteredOptions.length === 0 && (
              <p className="custom-dropdown-empty">No matching options</p>
            )}
            {filteredOptions.map((option) => (
              <button
                key={`${option.value}`}
                type="button"
                className={`custom-dropdown-option${
                  String(option.value) === String(value) ? " active" : ""
                }`}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

